import { useTranslation } from 'react-i18next'
import { Box, SxProps, useTheme } from '@mui/material'
import {
  AirAirHeatPumpIcon, AirWaterHeatPumpIcon, BatteryIcon, BoilerIcon, EnergyMeterIcon, EvChargerIcon, FcrBatteryIcon,
  GroundSourceHeatPumpIcon, InFloorHeatingIcon, OtherRelayIcon, RadiatorIcon, SolarPanelIcon, StorageTankIcon, TemperatureIcon
} from '../svg/icons'
import { useNavigate } from 'react-router-dom'
import { useApp } from '../hooks/useApp'

export function ApplianceTypeSelector() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const app = useApp()
  const theme = useTheme()

  const buttonSx: SxProps = {
    backgroundColor: 'radioSelectorBackground.main',
    color: '#EEEFF3', // Enion Päivä: icon outlines and label
    border: '2px solid transparent',
    borderRadius: '1rem',
    padding: '1rem 0.75rem',
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '0.9rem',
    lineHeight: 1.25,
    cursor: 'pointer',
    userSelect: 'none',
    // Icons fill their outlines with the tile colour and draw their energy element in the accent colour.
    '--enion-icon-cut': theme.palette.radioSelectorBackground.main,
    '--enion-icon-accent': theme.palette.secondary.main,
    ':hover': {
      borderColor: 'secondary.main',
    }
  }

  const disabledButtonSx: SxProps = Object.assign({}, buttonSx, {
    opacity: 0.42,
    cursor: 'inherit',
    ':hover': null
  })

  const iconSx: SxProps = {
    width: '4rem',
    height: '4rem',
    marginBottom: '0.75rem',
  }

  const hasFCR = app.userHas('FCR_BATTERIES')
  // Pilot: the Modbus heat pump (port 0x23) is hidden from everyone without the feature.
  const hasHeatPump = app.userHas('HEAT_PUMP_APPLIANCE')

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridColumnGap: '1rem',
      gridRowGap: '1rem',
    }}>
      <Box data-cy="button-Electricity Meter" sx={buttonSx} onClick={() => navigate('/appliances/new/ENERGY_METER')}>
        <Box sx={{ textAlign: 'center' }}><EnergyMeterIcon sx={iconSx}/></Box>
        {t('Electricity Meter')}
      </Box>
      <Box data-cy="button-EV Charger" sx={buttonSx} onClick={() => navigate('/appliances/new/EV_CHARGER')}>
        <Box sx={{ textAlign: 'center' }}><EvChargerIcon sx={iconSx}/></Box>
        {t('EV Charger')}
      </Box>
      <Box data-cy="button-Radiator" sx={buttonSx} onClick={() => navigate('/appliances/new/RADIATOR')}>
        <Box sx={{ textAlign: 'center' }}><RadiatorIcon sx={iconSx}/></Box>
        {t('Radiator')}
      </Box>
      <Box data-cy="button-Air-Air Heat Pump" sx={buttonSx} onClick={() => navigate('/appliances/new/AIR_AIR_HEAT_PUMP')}>
        <Box sx={{ textAlign: 'center' }}><AirAirHeatPumpIcon sx={iconSx}/></Box>
        {t('Air-Air Heat Pump')}
      </Box>
      <Box data-cy="button-Air-Water Heat Pump" sx={buttonSx} onClick={() => navigate('/appliances/new/AIR_WATER_HEAT_PUMP')}>
        <Box sx={{ textAlign: 'center' }}><AirWaterHeatPumpIcon sx={iconSx}/></Box>
        {t('Air-Water Heat Pump')}
      </Box>
      {hasHeatPump &&
        <Box data-cy="button-Air-to-water heat pump NEW" sx={buttonSx} onClick={() => navigate('/appliances/new/HEAT_PUMP')}>
          <Box sx={{ textAlign: 'center' }}><AirWaterHeatPumpIcon sx={iconSx}/></Box>
          {t('Air-to-water heat pump NEW')}
        </Box>
      }
      <Box data-cy="button-Ground Source Heat Pump" sx={buttonSx} onClick={() => navigate('/appliances/new/GROUND_SOURCE_HEAT_PUMP')}>
        <Box sx={{ textAlign: 'center' }}><GroundSourceHeatPumpIcon sx={iconSx}/></Box>
        {t('Ground Source Heat Pump')}
      </Box>
      <Box data-cy="button-Boiler" sx={buttonSx} onClick={() => navigate('/appliances/new/BOILER')}>
        <Box sx={{ textAlign: 'center' }}><BoilerIcon sx={iconSx}/></Box>
        {t('Boiler')}
      </Box>
      <Box data-cy="button-In-Floor Heating" sx={buttonSx} onClick={() => navigate('/appliances/new/IN_FLOOR_HEATING')}>
        <Box sx={{ textAlign: 'center' }}><InFloorHeatingIcon sx={iconSx}/></Box>
        {t('In-Floor Heating')}
      </Box>
      <Box data-cy="button-Storage Tank" sx={buttonSx} onClick={() => navigate('/appliances/new/STORAGE_TANK')}>
        <Box sx={{ textAlign: 'center' }}><StorageTankIcon sx={iconSx}/></Box>
        {t('Storage Tank')}
      </Box>
      <Box data-cy="button-Solar Inverter" sx={buttonSx} onClick={() => navigate('/appliances/new/SOLAR_PANEL')}>
        <Box sx={{ textAlign: 'center' }}><SolarPanelIcon sx={iconSx}/></Box>
        {t('Solar Inverter')}
      </Box>
      <Box data-cy="button-FCR Battery" sx={hasFCR ? buttonSx : disabledButtonSx} onClick={() => hasFCR && navigate('/appliances/new/FCR_BATTERY')}>
        <Box sx={{ textAlign: 'center' }}><FcrBatteryIcon sx={iconSx}/></Box>
        {t('Reserve Market Battery')}
      </Box>
      <Box data-cy="button-Energy Storage" sx={buttonSx} onClick={() => navigate('/appliances/new/BATTERY')}>
        <Box sx={{ textAlign: 'center' }}><BatteryIcon sx={iconSx}/></Box>
        {t('Battery or hybrid inverter')}
      </Box>
      <Box data-cy="button-Other Relay" sx={buttonSx} onClick={() => navigate('/appliances/new/OTHER_RELAY')}>
        <Box sx={{ textAlign: 'center' }}><OtherRelayIcon sx={iconSx}/></Box>
        {t('Other Relay')}
      </Box>
      <Box data-cy="button-Temperature Meter" sx={buttonSx} onClick={() => navigate('/appliances/new/TEMPERATURE')}>
        <Box sx={{ textAlign: 'center' }}><TemperatureIcon sx={iconSx}/></Box>
        {t('Temperature Meter')}
      </Box>
    </Box>
  )
}
